<!-- eslint-disable @typescript-eslint/no-unused-vars -->
<template>
  <v-progress-linear
    v-if="loadingTotal !== loadingCurrent"
    v-model="loadingPercent"
    color="secondary"
    rounded
    class="toolbar"
  />

  <v-alert
    v-if="showError"
    color="error"
    icon="fa-sharp-duotone fa-triangle-exclamation"
    :title="errorItem?.name ? errorItem?.name : errorItem?.message"
    :text="errorItem?.details ? errorItem?.details : errorItem?.message"
    transition="scroll-y-transition"
  >
    <template #append>
      <v-btn
        icon
        @click="clearError"
      >
        <v-icon icon="fa-sharp-duotone fa-times" />
        <v-tooltip>
          Clear Error
          <v-hotkey keys="escape" />
        </v-tooltip>
      </v-btn>
    </template>
  </v-alert>

  <v-sheet class="pa-0">
    <v-toolbar
      :collapse="hideToolbar"
      :class="hideToolbar ? 'toolbar' : undefined"
    >
      <v-btn to="/">
        <v-icon icon="fa-sharp-duotone fa-bars" />
        <v-tooltip>
          Show Songbook Listing
          <v-hotkey keys="backspace" />
        </v-tooltip>
      </v-btn>
      <v-toolbar-title
        v-if="!mobile"
        :text="parseTitle(appStore.fileName)"
      />
      <v-btn
        :disabled="firstSong"
        @click="prevSong"
      >
        <v-icon icon="fa-sharp-duotone fa-backward" />
        <v-tooltip>
          Prev Song
          <v-hotkey keys="pagedown" />
        </v-tooltip>
      </v-btn>

      <v-menu
        location="bottom"
        :max-height="getHeight"
      >
        <template #activator="{ props }">
          <v-btn
            variant="plain"
            v-bind="props"
            :disabled="noIndex"
          >
            <v-tooltip>
              Playlist
            </v-tooltip>
            <v-icon icon="fa-sharp-duotone fa-list-music" />
            <span v-if="!mobile">Songs</span>
          </v-btn>
        </template>
        <v-list
          v-model:selected="songSelect"
          theme="dark"
        >
          <v-list-item
            v-for="(song, n) in outlineTree"
            :key="n"
            base-color="secondary"
            color="accent"
            :value="n"
            @click.prevent="gotoSong(n, song.destination)"
          >
            <template #prepend>
              <v-avatar v-if="appStore.currentTrack === n">
                <v-icon icon="fa-sharp-duotone fa-circle-play" />
              </v-avatar>
              <v-avatar v-else>
                {{ n + 1 }}
              </v-avatar>
            </template>
            {{ song.title }}
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        :disabled="lastSong"
        @click="nextSong"
      >
        <v-icon icon="fa-sharp-duotone fa-forward" />
        <v-tooltip>
          Next Song
          <v-hotkey keys="pageup" />
        </v-tooltip>
      </v-btn>
      <v-divider
        vertical
        inset
      />
      <v-btn
        v-if="!mobile"
        v-tooltip:bottom="'Go to start'"
        icon="fa-sharp-duotone fa-backward-step"
        color="secondary"
        @click="getPage(1)"
      />

      <v-btn
        v-tooltip:bottom="'Prev Page'"
        icon="fa-sharp-duotone fa-angle-left"
        color="secondary"
        @click="prevPage"
      />

      <v-menu :close-on-content-click="false">
        <template #activator="{ props }">
          <v-btn
            v-tooltip:bottom="'Go to Page'"
            v-bind="props"
          >
            {{ appStore.currentPage }} / {{ pages }}
          </v-btn>
        </template>
        <v-card
          width="150"
          theme="dark"
        >
          <v-card-actions>
            <v-text-field
              v-model="newPage"
              width="180"
              label="Go To Page"
              :rules="[() => newPage <= pages || 'Cannot Go Past Last Page']"
              @update:model-value="appStore.currentPage = newPage"
            />
          </v-card-actions>
        </v-card>
      </v-menu>

      <v-btn
        v-tooltip:bottom="'Next Page'"
        icon="fa-sharp-duotone fa-angle-right"
        color="secondary"
        @click="nextPage"
      />

      <v-btn
        v-if="!mobile"
        v-tooltip:bottom="'Go to end'"
        icon="fa-sharp-duotone fa-forward-step"
        color="secondary"
        @click="getPage(pages)"
      />
      <v-spacer />

      <Timer @time-reached="timeCallback" />

      <v-btn @click="saveInfo">
        <v-icon icon="fa-sharp-duotone fa-file-brackets-curly" />
        <v-tooltip>
          Save track info
          <v-hotkey keys="ctrl+s" />
        </v-tooltip>
      </v-btn>

      <v-btn @click="saveFile">
        <v-icon icon="fa-sharp-duotone fa-file-pdf" />
        <v-tooltip>
          Download PDF
          <v-hotkey keys="ctrl+p" />
        </v-tooltip>
      </v-btn>
    </v-toolbar>
  </v-sheet>

  <!-- <v-sheet @click="nextPage"> -->
  <v-sheet>
    <v-carousel
      v-model="model"
      :height="getHeight"
    >
      <template #prev="{ props }">
        <v-btn
          icon="fa-sharp-duotone fa-angle-left"
          variant="elevated"
          density="comfortable"
          @click="props.onClick"
        />
      </template>
      <template #next="{ props }">
        <v-btn
          icon="fa-sharp-duotone fa-angle-right"
          variant="elevated"
          density="comfortable"
          @click="props.onClick"
        />
      </template>

      <v-carousel-item
        v-for="(page, i) in pages"
        :key="i"
        :value="i"
      >
        <VuePDF
          ref="VPDF"
          :pdf="pdf"
          :page="page"
          :height="getHeight"
        />
        <!-- <VuePDF
      ref="VPDF"
      class="pageview"
      :pdf="pdf"
      :page="appStore.currentPage"
      :height="getHeight"
      @loaded="onLoaded"
    > -->
      </v-carousel-item>
      <!-- </v-sheet> -->
    </v-carousel>
    <!-- </v-container> -->
  </v-sheet>
</template>

<script setup lang="ts">
import { VuePDF, usePDF, type PDFDestination } from "@tato30/vue-pdf";
import {
  ref,
  computed,
  triggerRef,
  watchEffect,
  watch,
  onMounted,
  onUnmounted,
} from "vue";
import { useAppStore } from "../stores/app";
import "@tato30/vue-pdf/style.css";
import { useDisplay, useHotkey } from "vuetify";
import { useRoute } from "vue-router";
import { type Song, type ErrorItem } from "../components/Types";
import router from "../router";

const route = useRoute();
const { mobile } = useDisplay();
const appStore = useAppStore();
const VPDF = ref({});
const newPage = ref(1);

const fileTarget = computed(() => {
  return appStore.file;
});

const { pdf, info, pages, getPDFDestination } = usePDF(fileTarget, {
  onError: (error) => {
    console.log(error);
    errorItem.value = error;
    showError.value = true;
  },
  onProgress: (prog) => {
    loadingTotal.value = prog.total;
    loadingCurrent.value = prog.loaded;
  },
});

const outlineTree = ref([] as Song[]);

const firstSong = computed(() => {
  return appStore.currentTrack === 0;
});

const lastSong = computed(() => {
  return appStore.currentTrack === outlineTree.value.length - 1;
});

const model = computed({
  get() {
    return appStore.currentPage - 1;
  },
  set(newValue) {
    appStore.setPage(newValue + 1);
  },
});

const songIndex = computed(() => {
  return appStore.currentTrack;
});
const songSelect = ref([0]);
const hideToolbar = ref(false);
const showError = ref(false);
const errorItem = ref<ErrorItem | null>(null);
const loadingTotal = ref(0);
const loadingCurrent = ref(0);

const loadingPercent = computed(() => {
  return (loadingCurrent.value * 100) / loadingTotal.value;
});

const cleanupFunctions = ref<Array<() => void>>([]);

function setFileName() {
  appStore.file = "https://data.tdot.men/pdf/" + route.params.file + ".pdf";
  appStore.fileName = Array.isArray(route.params.file)
    ? route.params.file[0]
    : route.params.file;
}

onMounted(() => {
  cleanupFunctions.value.forEach((cleanup) => cleanup());
  cleanupFunctions.value = [];
  cleanupFunctions.value.push(
    useHotkey("ctrl+s", () => {
      saveInfo();
    })
  );
  cleanupFunctions.value.push(
    useHotkey("ctrl+p", () => {
      saveFile();
    })
  );
  cleanupFunctions.value.push(
    useHotkey("backspace", () => {
      router.push("/");
    })
  );

  cleanupFunctions.value.push(
    useHotkey("arrowleft", () => {
      prevPage();
    })
  );
  cleanupFunctions.value.push(
    useHotkey("arrowright", () => {
      nextPage();
    })
  );
  cleanupFunctions.value.push(
    useHotkey("pagedown", () => {
      prevSong();
    })
  );
  cleanupFunctions.value.push(
    useHotkey("pageup", () => {
      nextSong();
    })
  );
  cleanupFunctions.value.push(
    useHotkey("escape", () => {
      clearError();
    })
  );

  onResize();

  // console.log("gotosong "+appStore.currentTrack)
});

onUnmounted(() => {
  appStore.setPage(1);
  appStore.setTrack(0);
  appStore.resetFile();
});

watch(() => route.params.file, setFileName, { immediate: true });

watchEffect(() => {
  outlineTree.value = [];
  if (
    info.value &&
    "outline" in info.value &&
    info.value.outline !== undefined
  ) {
    outlineTree.value = info.value?.outline.map(function convert(node) {
      return {
        title: node.title,
        destination: getPDFDestination(node.dest),
        items: node.items.map((item) => {
          return convert(item);
        }),
      };
    });
    // appStore.setPage(outlineTree.value[songIndex.value].destination.pageIndex);
  }

  // if (outlineTree.value && outlineTree.value[appStore.currentTrack]?.destination && loadingPercent.value === 100)
  // {
  //   gotoSong(appStore.currentTrack, outlineTree.value[appStore.currentTrack].destination);

  // }
});
triggerRef(info);

const noIndex = computed(() => {
  if (info.value && "outline" in info.value && info.value.outline?.length > 0) {
    return false;
  } else {
    return true;
  }
});

const getHeight = computed(() => {
  if (hideToolbar.value === true) return window.innerHeight;
  else return window.innerHeight - 60;
});

// const getWidth = computed(() => {
//   return window.innerWidth;
// })

function onResize() {}

function parseTitle(book: string) {
  if (book.includes("-")) {
    return book
      .trim()
      .replace("-", " ")
      .replace(/\B([A-Z])\B/g, " $1");
  } else {
    return book.trim().replace(/\B([A-Z])\B/g, " $1");
  }
}
async function gotoPage(dest: PDFDestination) {
  try {
    const destination = await dest;
    appStore.currentPage = destination.pageIndex + 1;
  } catch (err) {
    errorItem.value = err;
    showError.value = true;
  }
}

function timeCallback() {
  nextPage();
}

async function gotoSong(index: number, dest: PDFDestination) {
  try {
    const destination = await dest;
    appStore.currentPage = destination.pageIndex + 1;
    appStore.setTrack(index);
    // songIndex.value = index;
  } catch (err) {
    errorItem.value = err;
    showError.value = true;
  }
}

function clearError() {
  errorItem.value = {};
  showError.value = false;
}

function prevSong() {
  try {
    if (songIndex.value > 0) {
      appStore.setTrack(songIndex.value - 1);
      // songIndex.value--;
      gotoPage(outlineTree.value[songIndex.value].destination);

      songSelect.value = [songIndex.value];
    }
  } catch (err) {
    errorItem.value = err;
    showError.value = true;
  }
}

function prevPage() {
  if (appStore.currentPage > 1) {
    appStore.currentPage--;
  }
}

function nextPage() {
  if (appStore.currentPage < pages.value) {
    appStore.currentPage++;
  }
}

function nextSong() {
  try {
    if (songIndex.value < outlineTree.value.length) {
      // songIndex.value++;
      appStore.setTrack(songIndex.value + 1);
      gotoPage(outlineTree.value[songIndex.value].destination);
      songSelect.value = [songIndex.value];
    }
  } catch (err) {
    errorItem.value = err;
    showError.value = true;
  }
}

function getPage(n: number) {
  appStore.currentPage = n;
}

function saveFile() {
  const element = document.createElement("a");
  element.setAttribute("href", fileTarget.value);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
}

function saveInfo() {
  if (info.value !== null && info.value.outline !== null) {
    const songListing: { title: string; destination: PDFDestination }[] = [];
    info.value.outline.forEach((o: { title: string; dest: PDFDestination }) => {
      const u: { title: string; destination: PDFDestination } = {
        title: o.title,
        destination: o.dest,
      };

      songListing.push(u);
    });

    const text = JSON.stringify(songListing);
    const filename = appStore.fileName;
    const element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:application/json;charset=utf-8," + encodeURIComponent(text)
    );
    element.setAttribute("download", filename);

    element.style.display = "none";
    document.body.appendChild(element);

    element.click();
    document.body.removeChild(element);
  } else {
    errorItem.value = {
      name: "No Outline",
      details: "Unable to create destination information",
    };
    showError.value = true;
  }
}
</script>

<style lang="scss">
.toolbar {
  position: fixed !important;
  z-index: 10;
}

.pdfFile {
  height: 100dvh !important;
  max-height: 100dvh !important;
  width: auto;
}

.lds-ring,
.lds-ring div {
  box-sizing: border-box;
}
.lds-ring {
  display: inline-block;
  position: relative;
  margin-top: 100px;
  width: 80px;
  height: 80px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid currentColor;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: currentColor transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
